/**
 * Step 2: owns the event list, filters, selection rules and submission; the
 * child components only render. Already added events are read only. Single
 * organization events may be exclusive, so they are hidden by default, left out of
 * select all, and need a confirmed selection each.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Loader, Message } from 'semantic-ui-react';
import { I18n, Translate } from 'react-redux-i18n';
import { addOrganizationToEventsBatch, getOrganizationEvents } from '../api';
import { ADD_BATCH_SIZE, i18nKeys } from '../constants';
import {
  bulkSelectableIds,
  chunk,
  defaultFilters,
  distinctValues,
  emptyBulkAddResult,
  filterEvents,
  getHeaderCheckboxState,
  isExclusive,
  mergeBulkAddResults,
  sortEvents,
  startOfToday
} from '../helpers';
import {
  AddSummary,
  Organization,
  OrganizationEvent,
  EventFilters
} from '../types';
import SelectedOrganizationCard from './SelectedOrganizationCard';
import EventFilterBar from './EventFilterBar';
import EventTable from './EventTable';
import ExclusiveEventModal from './ExclusiveEventModal';
import SelectionFooter from './SelectionFooter';
import AddSummaryPanel from './AddSummaryPanel';
import styles from '../AddOrganizationToEvents.module.css';

interface EventSelectionProps {
  organization: Organization;
  /** The search to return to, with the user's query when they came from it. */
  searchPath: string;
  onCancel: () => void;
}

const EventSelection: React.FC<EventSelectionProps> = ({
  organization,
  searchPath,
  onCancel
}) => {
  const [events, setEvents] = useState<OrganizationEvent[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [filters, setFilters] = useState<EventFilters>(defaultFilters);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [pendingExclusive, setPendingExclusive] =
    useState<OrganizationEvent | null>(null);
  const [progress, setProgress] = useState<number | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [summary, setSummary] = useState<AddSummary | null>(null);

  const loadEvents = useCallback(async () => {
    setEvents(null);
    setLoadError(null);
    try {
      setEvents(await getOrganizationEvents(organization.id));
    } catch {
      setLoadError(I18n.t(i18nKeys.EVENTS.LOAD_FAILED));
    }
  }, [organization.id]);

  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  // Reloads in place after a submit, so the table and filters stay put and
  // newly added events turn into read-only "Already added" rows.
  const refreshEvents = async () => {
    try {
      setEvents(await getOrganizationEvents(organization.id));
    } catch {
      setLoadError(I18n.t(i18nKeys.EVENTS.LOAD_FAILED));
    }
  };

  const allEvents = useMemo(() => events ?? [], [events]);
  const visible = useMemo(() => {
    const today = startOfToday();
    return filterEvents(sortEvents(allEvents, today), filters, selected, today);
  }, [allEvents, filters, selected]);
  const header = getHeaderCheckboxState(visible, selected);

  const alreadyAddedCount = allEvents.filter(
    (event) => event.alreadyAdded
  ).length;
  const singleOrganizationCount = allEvents.filter(
    (event) => isExclusive(event) && !event.alreadyAdded
  ).length;

  const countryOptions = useMemo(
    () => distinctValues(allEvents.map((event) => event.country)),
    [allEvents]
  );
  const stateOptions = useMemo(
    () =>
      distinctValues(
        allEvents
          .filter((event) => event.country === filters.country)
          .map((event) => event.state)
      ),
    [allEvents, filters.country]
  );

  // Selections survive filter changes, so several searches can build one batch.
  const toAdd = allEvents.filter(
    (event) => selected.has(event.id) && !event.alreadyAdded
  );
  const isSubmitting = progress !== null;

  const setFilter = <K extends keyof EventFilters>(
    key: K,
    value: EventFilters[K]
  ) =>
    setFilters((current) => ({
      ...current,
      [key]: value,
      // A state belongs to one country, so a new country clears it.
      ...(key === 'country' ? { state: '' } : {})
    }));

  const setSelection = (ids: number[], isSelected: boolean) =>
    setSelected((current) => {
      const next = new Set(current);
      ids.forEach((id) => (isSelected ? next.add(id) : next.delete(id)));
      return next;
    });

  const handleToggle = (event: OrganizationEvent, isSelected: boolean) => {
    if (isSelected && isExclusive(event)) {
      setPendingExclusive(event);
      return;
    }
    setSelection([event.id], isSelected);
  };

  const handleSubmit = async () => {
    const batches = chunk(
      toAdd.map((event) => event.id),
      ADD_BATCH_SIZE
    );
    let total = emptyBulkAddResult();
    setSubmitError(null);
    setSummary(null);
    setProgress(0);

    try {
      for (const batch of batches) {
        total = mergeBulkAddResults(
          total,
          await addOrganizationToEventsBatch(organization.id, batch)
        );
        setProgress(
          total.added.length + total.alreadyAdded.length + total.skipped.length
        );
      }
    } catch {
      // Earlier batches are committed, so refresh the list to show where it stopped.
      setSubmitError(
        I18n.t(i18nKeys.EVENTS.SUBMIT_FAILED, { count: total.added.length })
      );
      setSelected(new Set());
      setProgress(null);
      refreshEvents();
      return;
    }

    setSummary({
      added: total.added.length,
      unchanged: alreadyAddedCount + total.alreadyAdded.length,
      skipped: total.skipped.length
    });
    setSelected(new Set());
    setProgress(null);
    refreshEvents();
  };

  const dismissSummary = useCallback(() => setSummary(null), []);

  return (
    <>
      {summary && (
        <AddSummaryPanel
          organizationName={organization.name}
          summary={summary}
          onDismiss={dismissSummary}
        />
      )}

      <SelectedOrganizationCard
        organization={organization}
        alreadyInCount={events ? alreadyAddedCount : null}
        backTo={searchPath}
        disabled={isSubmitting}
        onChange={onCancel}
      />

      {loadError && (
        <Message negative>
          <p>{loadError}</p>
          <Button size="small" onClick={loadEvents}>
            <Translate value={i18nKeys.EVENTS.RETRY} />
          </Button>
        </Message>
      )}

      {!events && !loadError && <Loader active inline="centered" />}

      {events && (
        <>
          <EventFilterBar
            filters={filters}
            countryOptions={countryOptions}
            stateOptions={stateOptions}
            singleOrganizationCount={singleOrganizationCount}
            alreadyAddedCount={alreadyAddedCount}
            onChange={setFilter}
          />

          <p className={styles.muted}>
            {I18n.t(i18nKeys.EVENTS.SHOWING, {
              visible: visible.length,
              total: allEvents.length
            })}
          </p>

          <EventTable
            events={visible}
            selected={selected}
            header={header}
            disabled={isSubmitting}
            onToggleAll={() =>
              setSelection(bulkSelectableIds(visible), !header.checked)
            }
            onToggle={handleToggle}
          />

          {submitError && (
            <Message negative>
              <p>{submitError}</p>
            </Message>
          )}

          <SelectionFooter
            count={toAdd.length}
            progress={progress}
            onCancel={onCancel}
            onSubmit={handleSubmit}
          />
        </>
      )}

      <ExclusiveEventModal
        event={pendingExclusive}
        onCancel={() => setPendingExclusive(null)}
        onConfirm={(event) => {
          setSelection([event.id], true);
          setPendingExclusive(null);
        }}
      />
    </>
  );
};

export default EventSelection;
